/**
 * Project Variant Descriptors
 *
 * The Database applet supports multiple "project variants" (currently STC and Military).
 * Each variant stores its data in parallel Firebase collections and uses different
 * number prefixes, but otherwise shares the same UI / actions / forms.
 *
 * To stay DRY, every entity module exports `make<Entity>Routes(variant)`,
 * `make<Entity>Attachable(variant)` and `make<ActionName>(variant)` factories
 * that take a single descriptor. A child entity's variant is derived from its
 * parent at action time via `getVariantByParentType(meta.ParentType)`.
 *
 * @typedef {Object} Variant
 * @property {"stc"|"military"} key             - canonical variant identifier
 * @property {string}           label           - human-readable applet name
 * @property {string}           app             - meta.app value (single token, no spaces)
 * @property {string}           appIcon         - icon for the app switcher
 * @property {number}           appSwitcherPosition
 * @property {string}           collectionPrefix - "" or "Military" — prepended to entity collection names
 * @property {string}           numberPrefix     - prefix used in DesignChange numbers (e.g. "" or "M-")
 * @property {string}           docPrefix        - prefix used in Document numbers (from appConfig)
 * @property {string}           drawingPrefix    - prefix used in Drawing numbers
 * @property {string}           approvalNumber   - default EASA / approval number
 * @property {string}           security         - meta.security base path
 */

import { appConfig } from "../../../utils.js";

/**
 * STC (civilian) variant — the historical default. Uses the un-prefixed
 * Firebase collections.
 */
export const STC_VARIANT = Object.freeze({
    key: "stc",
    label: "Database",
    app: "Database",
    appIcon: "database",
    appSwitcherPosition: 1,
    collectionPrefix: "",
    numberPrefix: "",
    docPrefix: appConfig.docPrefix || "",
    drawingPrefix: "",
    approvalNumber: appConfig.approvalNumber || "",
    security: "Database.Read",
});

/**
 * Military variant — parallel Firebase collections under `Database/Military<Entity>`.
 * Number prefixes pulled from `militaryDocPrefix` / `militaryAppPrefix` on the
 * application config.
 */
export const MILITARY_VARIANT = Object.freeze({
    key: "military",
    label: "Military Database",
    app: "MilitaryDatabase",
    appIcon: "jet-fighter-up",
    appSwitcherPosition: 9,
    collectionPrefix: "Military",
    numberPrefix: "M-",
    docPrefix: appConfig.militaryDocPrefix || "",
    drawingPrefix: appConfig.militaryAppPrefix || "",
    approvalNumber: appConfig.approvalNumber || "",
    security: "Military.Read",
    variant: "military",
});

/**
 * Returns true when the current build's appConfig has the prefixes required
 * to register the Military variant. STC builds (stc-prod, test) provide
 * `militaryDocPrefix`; DOA / Sigmatmod builds do not, so the Military app is
 * suppressed from registration on those builds.
 */
export const isMilitaryEnabled = () => Boolean(appConfig.militaryDocPrefix);

/**
 * All registered variants for the current build.
 * @returns {Variant[]}
 */
export const allVariants = () => isMilitaryEnabled()
    ? [STC_VARIANT, MILITARY_VARIANT]
    : [STC_VARIANT];

// ---------------------------------------------------------------------------
// Path helpers — single source of truth for Firebase locations
// ---------------------------------------------------------------------------

/** `Database/Documents` or `Database/MilitaryDocuments`. No trailing slash. */
export const collection = (variant, entity) =>
    `Database/${variant.collectionPrefix}${entity}`;

/** `Database/Refs/Documents` or `Database/Refs/MilitaryDocuments`. */
export const refs = (variant, entity) =>
    `Database/Refs/${variant.collectionPrefix}${entity}`;

/** `Database/SuperRefs/Documents` or `Database/SuperRefs/MilitaryDocuments`. */
export const superRefs = (variant, entity) =>
    `Database/SuperRefs/${variant.collectionPrefix}${entity}`;

/**
 * The `ParentType` string used when an entity stores a backref to its parent.
 * e.g. `DesignChanges` or `MilitaryDesignChanges`. This is also written to
 * `meta.ParentType` so children can resolve the variant from the route.
 */
export const parentTypeFor = (variant, entity) =>
    `${variant.collectionPrefix}${entity}`;

/**
 * URL path piece for an entity's list/detail route, e.g.
 * `/Database/DesignChanges` or `/Database/MilitaryDesignChanges`. The leading
 * segment is always `Database` so existing user bookmarks under that namespace
 * keep working — variant separation is by entity name, not URL root.
 */
export const routePath = (variant, entity) =>
    `/Database/${variant.collectionPrefix}${entity}`;

// ---------------------------------------------------------------------------
// Elasticsearch attachable-table sourcing
// ---------------------------------------------------------------------------
//
// Attachable tables can source their rows from the tenant's Elasticsearch
// index instead of per-path RTDB watches. This only applies to indexed types:
// the whitelist below mirrors DATABASE_ES_TYPES in the functions constants, and
// only the un-prefixed (STC) variant is indexed — Military/prefixed collections
// are not, so they always fall back to RTDB.

/** Base entity types indexed in Elasticsearch (mirror of functions constants). */
export const DATABASE_ES_TYPES = Object.freeze([
    "Documents",
    "Repairs",
    "DesignChanges",
    "JobCards",
    "FlightConditions",
    "Drawings",
    "DQNs",
    "ServiceBulletins",
    "Commercials",
    "PurchaseOrders",
    "Arrangements",
    "DOIs",
    "Concessions",
    "VariationOrders",
    "Effectivities",
    "EADTs",
    "EARs",
    "Manufacturers",
    "ApprovedPartNumbers",
    "SOWs",
    "SADDs",
]);

const DATABASE_ES_TYPE_SET = new Set(DATABASE_ES_TYPES);

/**
 * Resolve the Elasticsearch type an attachable table should query, or `null`
 * when it must fall back to RTDB. Returns null unless the build has ES lists
 * enabled, the (optional) variant is un-prefixed, and the derived type is
 * indexed.
 *
 * The type is taken from an explicit `attachable.esType`, otherwise derived
 * from `attachable.location` by stripping the leading `Database/` and trailing
 * slash (e.g. `Database/DesignChanges/` -> `DesignChanges`).
 *
 * @param {object} attachable            - the attachable descriptor
 * @param {Variant} [variant]            - the owning variant, if any
 * @returns {string|null}
 */
export const esTypeForAttachable = (attachable, variant) => {
    if (!appConfig.useElasticsearchLists) return null;
    if (!attachable) return null;
    if (variant && variant.collectionPrefix !== "") return null;

    const explicit = attachable.esType;
    const derived =
        explicit ||
        String(attachable.location || "")
            .replace(/^Database\//, "")
            .replace(/\/+$/, "");

    if (!derived || derived.includes("/")) return null;
    return DATABASE_ES_TYPE_SET.has(derived) ? derived : null;
};

/**
 * Resolve the Elasticsearch type for a raw Firebase location such as
 * `Database/Documents/{id}` (an item path) or `Database/Documents` (a
 * collection path). Returns null unless ES lists are enabled and the first path
 * segment after `Database/` is an indexed, un-prefixed (non-Military) type.
 *
 * @param {string} location
 * @returns {string|null}
 */
export const esTypeForLocation = (location) => {
    if (!appConfig.useElasticsearchLists) return null;
    const rest = String(location || "")
        .replace(/^\/+/, "")
        .replace(/^Database\//, "");
    const type = rest.split("/")[0];
    if (!type) return null;
    return DATABASE_ES_TYPE_SET.has(type) ? type : null;
};

// ---------------------------------------------------------------------------
// Variant resolution from a parent context
// ---------------------------------------------------------------------------

/**
 * Resolve the variant a child entity should write into, based on its parent's
 * `ParentType` string (set by the parent route's `meta.formMetadata.ParentType`).
 *
 * Any ParentType beginning with `Military` belongs to the Military variant;
 * everything else (including non-variant parents like `Commercials`) is STC.
 *
 * @param {string|undefined} parentType
 * @returns {Variant}
 */
export const getVariantByParentType = (parentType) => {
    if (typeof parentType === "string" && parentType.startsWith("Military")) {
        return MILITARY_VARIANT;
    }
    return STC_VARIANT;
};

// ---------------------------------------------------------------------------
// Counts (shared "type" collections — DocumentTypes, ATAChapters, DrawingTypes)
// ---------------------------------------------------------------------------
//
// Counts move to a nested map: `Counts: { stc: N, military: N }`.
// Reads fall back to the legacy field shape (`Count`, `CountDrawings`,
// `CountMilitaryDesignChanges`, etc.) so the UI keeps working on records
// that have not yet been migrated. Writes update both the new map and the
// legacy field for the duration of the migration window — once the migration
// script has been run against all environments the legacy writes can be
// dropped.

/**
 * Build the legacy field name for a given variant + base counter.
 * STC → `${baseKey}` (e.g. `CountDrawings`, `Count`).
 * Military → `${baseKey.replace(/^Count/, "CountMilitary")}` so we get
 * `CountMilitaryDrawings` / `CountMilitary` (matching existing data).
 */
const legacyCountField = (variant, baseKey) => {
    if (variant.key === "stc") return baseKey;
    return baseKey.replace(/^Count/, "CountMilitary");
};

/**
 * Read a counter from a record on a shared "type" collection (e.g. an
 * ATAChapter doc, a DocumentType doc). Prefers the new `Counts` map and
 * falls back to the legacy field name.
 *
 * @param {object} record   - the type record from Firebase / store
 * @param {Variant} variant
 * @param {string}  baseKey - the legacy STC field name (e.g. "CountDrawings", "Count")
 * @returns {number}
 */
export const readCount = (record, variant, baseKey) => {
    if (!record) return 0;
    const fromMap = record?.Counts?.[baseKey]?.[variant.key];
    if (typeof fromMap === "number") return fromMap;
    const legacy = record?.[legacyCountField(variant, baseKey)];
    return typeof legacy === "number" ? parseInt(legacy) : parseInt(legacy || 0);
};

/**
 * Returns the set of Firebase update paths required to persist `nextValue`
 * for the given counter. Writes both the new `Counts` map and the legacy
 * field so an in-flight migration cannot regress.
 *
 * Usage:
 *   const updates = countUpdates(`Database/ATAChapters/${id}`, variant, "CountDrawings", n + 1);
 *   await Promise.all(Object.entries(updates).map(([loc, val]) => db.set(loc, val)));
 *
 * @returns {Record<string, number>} map of firebase location → value
 */
export const countUpdates = (typeLocation, variant, baseKey, nextValue) => ({
    [`${typeLocation}/Counts/${baseKey}/${variant.key}`]: nextValue,
    [`${typeLocation}/${legacyCountField(variant, baseKey)}`]: nextValue,
});

// ---------------------------------------------------------------------------
// Global counters (`counts/database/<entity>/count` style)
// ---------------------------------------------------------------------------
//
// Many entity actions claim a number with db.utils.allocateCount(path), which
// transactionally bumps a global counter. Military
// records need their own counter stream so STC and Military numbers do not
// collide. We keep the STC paths exactly as they were (no migration needed)
// and prefix the path segment for Military.

/**
 * Build a global-counter location for the given variant.
 *
 * @param {Variant} variant
 * @param {string}  basePath - the legacy STC counter path,
 *                             e.g. `"counts/database/concession/count"`
 *                             or `"counts/ears/count"`.
 * @returns {string}
 *
 * @example
 *   globalCounterPath(STC_VARIANT, "counts/database/concession/count")
 *     // → "counts/database/concession/count"
 *   globalCounterPath(MILITARY_VARIANT, "counts/database/concession/count")
 *     // → "counts/database/military_concession/count"
 *   globalCounterPath(MILITARY_VARIANT, "counts/ears/count")
 *     // → "counts/military_ears/count"
 */
export const globalCounterPath = (variant, basePath) => {
    if (variant.key === "stc") return basePath;
    // Inject `military_` ahead of the entity segment, which is the segment
    // immediately before the trailing `/count`.
    const parts = basePath.split("/");
    const tail = parts[parts.length - 1]; // e.g. "count"
    const entity = parts[parts.length - 2]; // e.g. "concession"
    parts[parts.length - 2] = `military_${entity}`;
    return parts.join("/");
};

/**
 * Read the current value of a global counter from Vuex store state.
 * Mirrors the existing `store.state.counts.<...>.<entity>.count` shape.
 *
 * @param {object}  store
 * @param {Variant} variant
 * @param {string[]} keys - path segments under `store.state.counts`,
 *                          e.g. `["database", "concession"]`
 * @returns {number}
 */
export const readGlobalCount = (store, variant, keys) => {
    const segments = variant.key === "stc"
        ? keys
        : [...keys.slice(0, -1), `military_${keys[keys.length - 1]}`];
    let node = store?.state?.counts;
    for (const k of segments) {
        if (!node) return 0;
        node = node[k];
    }
    return parseInt(node?.count || 0);
};



